import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import './SettingsHome.css';

const AUTH0_DOMAIN = 'appliance-monitor.us.auth0.com';
const AUTH0_CLIENT_ID = 'hvVYuNvAioWz7klDFTVxgPw2MagVMSVx';
const DEFAULT_DB_CONNECTION = 'Username-Password-Authentication';

function profileValue(value) {
    if (typeof value === 'string' && value.trim().length > 0) {
        return value;
    }

    return 'Not provided';
}

function SettingsHome() {
    const { user, isAuthenticated, isLoading } = useAuth0();
    const [imageLoadError, setImageLoadError] = React.useState(false);
    const [isSendingReset, setIsSendingReset] = React.useState(false);
    const [passwordMessage, setPasswordMessage] = React.useState(null);

    React.useEffect(() => {
        setImageLoadError(false);
    }, [user?.picture]);

    if (isLoading) {
        return null;
    }

    if (!isAuthenticated) {
        return (
            <main className="settings-page">
                <section className="settings-shell">
                    <div className="settings-unauth">
                        <h2>Settings</h2>
                        <p>Please sign in to view your profile settings.</p>
                    </div>
                </section>
            </main>
        );
    }

    const hasPicture = typeof user?.picture === 'string' && user.picture.trim().length > 0;
    const pictureUrl = hasPicture ? user.picture : null;
    const displayName = profileValue(user?.name || user?.nickname || user?.email);
    const isPasswordUser = typeof user?.sub === 'string' && user.sub.startsWith('auth0|');
    const canResetPassword = isPasswordUser && typeof user?.email === 'string' && user.email.trim().length > 0;

    const handlePasswordReset = async () => {
        if (!canResetPassword || isSendingReset) {
            return;
        }

        try {
            setIsSendingReset(true);
            setPasswordMessage(null);

            const response = await fetch(`https://${AUTH0_DOMAIN}/dbconnections/change_password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    client_id: AUTH0_CLIENT_ID,
                    email: user.email,
                    connection: user?.identities?.[0]?.connection || DEFAULT_DB_CONNECTION,
                }),
            });

            if (!response.ok) {
                throw new Error('Request failed');
            }

            setPasswordMessage({
                type: 'success',
                text: 'Password reset email sent. Check your inbox for the reset link.',
            });
        } catch (error) {
            setPasswordMessage({
                type: 'error',
                text: 'Could not send reset email right now. Please try again shortly.',
            });
        } finally {
            setIsSendingReset(false);
        }
    };

    return (
        <main className="settings-page">
            <section className="settings-shell">
                <div className="settings-card">
                    <div className="settings-hero">
                        <div className="settings-avatar-wrap">
                            {pictureUrl && !imageLoadError ? (
                                <img
                                    className="settings-avatar"
                                    src={pictureUrl}
                                    alt={`${displayName} profile`}
                                    onError={() => setImageLoadError(true)}
                                />
                            ) : (
                                <div className="settings-avatar-placeholder" aria-hidden="true">
                                    {displayName.charAt(0).toUpperCase()}
                                </div>
                            )}
                        </div>
                        <div className="settings-title-block">
                            <p className="settings-eyebrow">User Settings</p>
                            <h1>{profileValue(user.name)}</h1>
                        </div>
                    </div>

                    <dl className="settings-field-list">
                        <div className="settings-field-row">
                            <dt>Name</dt>
                            <dd>{profileValue(user.name)}</dd>
                        </div>
                        <div className="settings-field-row">
                            <dt>Email</dt>
                            <dd>{profileValue(user.email)}</dd>
                        </div>
                        <div className="settings-field-row">
                            <dt>Nickname</dt>
                            <dd>{profileValue(user.nickname)}</dd>
                        </div>
                        <div className="settings-field-row">
                            <dt>Given Name</dt>
                            <dd>{profileValue(user.given_name)}</dd>
                        </div>
                        <div className="settings-field-row">
                            <dt>Family Name</dt>
                            <dd>{profileValue(user.family_name)}</dd>
                        </div>
                    </dl>

                    <div className="settings-actions">
                        {canResetPassword ? (
                            <button
                                type="button"
                                className="settings-action-button"
                                onClick={handlePasswordReset}
                                disabled={isSendingReset}
                            >
                                {isSendingReset ? 'Sending reset email...' : 'Change Password'}
                            </button>
                        ) : (
                            <p className="settings-note">
                                Password changes are managed by your identity provider for this account.
                            </p>
                        )}
                        {passwordMessage && (
                            <p className={`settings-note settings-note-${passwordMessage.type}`}>
                                {passwordMessage.text}
                            </p>
                        )}
                    </div>
                </div>
            </section>
        </main>
    );
}

export default SettingsHome;
